import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Save, FolderOpen, Trash2, RotateCcw, Loader2, Info, FileDown, Link2, Link2Off, Copy, ExternalLink, Wrench } from "lucide-react";
import { api, errorMessage } from "../../lib/api";
import { authApi } from "../../lib/auth";
import { formatPrice, sortByOrder } from "../../lib/matrix";
import { localizePlan, localizeAddon, regionMeta, readRegion } from "../../lib/pricing";
import { computePlan, defaultMoves, mergeMoves } from "../../lib/planner";
import { downloadDocumentPdf } from "../../lib/share";
import { appendixLists, fetchAppendix } from "../../lib/appendix";
import PlannerMove from "./PlannerMove";
import ProposalCard from "./ProposalCard";

const signed = (v, symbol, est) => (v === null || v === undefined ? "—" : `${v > 0 ? "+" : v < 0 ? "−" : ""}${formatPrice(Math.abs(v), symbol, est)}`);

export default function Planner({ tenant, scan, isAdmin, prefill }) {
  const [dataset, setDataset] = useState(null);
  const [region, setRegion] = useState("us");
  const [priceMode, setPriceMode] = useState("teams");
  const [moves, setMoves] = useState(() => defaultMoves(scan.summary));
  const [scenarios, setScenarios] = useState([]);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [includeAppendix, setIncludeAppendix] = useState(true);
  const [appendix, setAppendix] = useState([]);
  const [sharing, setSharing] = useState("");
  const cardRef = useRef(null);
  const appliedPrefill = useRef(null);

  useEffect(() => {
    if (!prefill || prefill.ts === appliedPrefill.current) return;
    appliedPrefill.current = prefill.ts;
    setMoves((list) => mergeMoves(list, prefill.moves));
    toast.success(prefill.label || "Planner pre-filled", { description: `${prefill.moves.length} move${prefill.moves.length === 1 ? "" : "s"} added - adjust targets or seats before saving.` });
  }, [prefill]);

  useEffect(() => {
    if (exporting !== "render" || !cardRef.current) return undefined;
    const node = cardRef.current;
    const run = async () => {
      try {
        await downloadDocumentPdf(node, `${tenant.name}-proposal`);
        toast.success("Proposal PDF downloaded", { description: appendix.length ? `Includes an appendix with ${appendix.length} affected-user list${appendix.length === 1 ? "" : "s"}.` : undefined });
      } catch (err) {
        toast.error("Could not create the PDF", { description: err?.message });
      } finally {
        setExporting(false);
      }
    };
    const t = setTimeout(run, 50);
    return () => clearTimeout(t);
  }, [exporting, tenant.name, appendix]);

  useEffect(() => {
    api
      .matrix()
      .then((d) => {
        setDataset(d);
        setRegion(readRegion(d));
      })
      .catch((err) => toast.error("Could not load pricing data", { description: errorMessage(err) }));
    authApi.tenants.scenarios(tenant.id).then(setScenarios).catch(() => {});
  }, [tenant.id]);

  const plans = useMemo(() => (dataset ? sortByOrder(dataset.plans).map((p) => localizePlan(p, region)) : []), [dataset, region]);
  const addons = useMemo(() => (dataset ? dataset.addons.map((a) => localizeAddon(a, region)) : []), [dataset, region]);
  const pricing = useMemo(() => regionMeta(dataset, region), [dataset, region]);
  const symbol = pricing.symbol || "$";
  const plan = useMemo(() => (dataset ? computePlan({ summary: scan.summary, moves, plans, addons, rows: dataset.rows, priceMode }) : null), [dataset, scan, moves, plans, addons, priceMode]);

  const updateMove = (m) => setMoves((list) => list.map((x) => (x.from_sku === m.from_sku ? { ...m, remediation: x.remediation } : x)));
  const reset = () => setMoves(defaultMoves(scan.summary));

  const exportPdf = async () => {
    setExporting("prep");
    try {
      const lists = includeAppendix ? await fetchAppendix(scan.id, appendixLists(plan, scan.summary)) : [];
      setAppendix(lists.filter((l) => l.users.length));
      setExporting("render");
    } catch (err) {
      toast.error("Could not load the appendix", { description: errorMessage(err) });
      setExporting(false);
    }
  };

  const share = async (s, revoke = false) => {
    setSharing(s.id);
    try {
      const saved = revoke ? await authApi.tenants.unshareScenario(tenant.id, s.id) : await authApi.tenants.shareScenario(tenant.id, s.id);
      setScenarios((list) => list.map((x) => (x.id === saved.id ? saved : x)));
      if (!revoke) {
        const url = `${window.location.origin}/p/${saved.share_token}`;
        try {
          await navigator.clipboard.writeText(url);
          toast.success("Share link copied", { description: "Anyone with the link can view this proposal - no sign-in needed." });
        } catch {
          toast.success("Share link created");
        }
      } else {
        toast.success("Link revoked");
      }
    } catch (err) {
      toast.error("Could not update the share link", { description: errorMessage(err) });
    } finally {
      setSharing("");
    }
  };

  const copyLink = async (s) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/p/${s.share_token}`);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed - select the link text manually");
    }
  };

  const save = async () => {
    if (!name.trim()) return toast.error("Give the scenario a name");
    setSaving(true);
    try {
      const body = { name: name.trim(), scan_id: scan.id, region, price_mode: priceMode, moves: moves.map(({ remediation, ...m }) => ({ ...m, to_extras: m.to_extras || [] })), retire: [] };
      const existing = scenarios.find((s) => s.name.toLowerCase() === name.trim().toLowerCase());
      const saved = existing ? await authApi.tenants.updateScenario(tenant.id, existing.id, body) : await authApi.tenants.createScenario(tenant.id, body);
      setScenarios((list) => [saved, ...list.filter((s) => s.id !== saved.id)]);
      toast.success(existing ? "Scenario updated" : "Scenario saved");
    } catch (err) {
      toast.error("Could not save scenario", { description: errorMessage(err) });
    } finally {
      setSaving(false);
    }
    return undefined;
  };

  const load = (s) => {
    setMoves(s.moves.length ? s.moves : defaultMoves(scan.summary));
    setRegion(s.region || "us");
    setPriceMode(s.price_mode || "teams");
    setName(s.name);
    toast.success(`Loaded "${s.name}"`);
  };

  const remove = async (s) => {
    try {
      await authApi.tenants.deleteScenario(tenant.id, s.id);
      setScenarios((list) => list.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error("Could not delete", { description: errorMessage(err) });
    }
  };

  if (!dataset || !plan) return <div className="ws-empty">Loading pricing data...</div>;
  if (plan.lines.length === 0) {
    return (
      <div className="ws-empty glass" data-testid="planner-empty">
        <Info size={22} />
        <h3>No base suites recognized</h3>
        <p>The scan found no assigned base suites that map to the license matrix (E1/E3/E5, Business, Frontline...).</p>
      </div>
    );
  }

  return (
    <div className="ws-stack planner" data-testid="planner">
      <div className="planner-bar glass">
        <div className="planner-opts">
          <label className="ws-select">
            <span>Region</span>
            <select value={region} onChange={(e) => setRegion(e.target.value)} data-testid="planner-region">
              {dataset.regions.map((r) => (
                <option key={r.id} value={r.id}>
                  {`${r.label} (${r.currency})`}
                </option>
              ))}
            </select>
          </label>
          <div className="seg" role="group" aria-label="Suite pricing">
            <button type="button" aria-pressed={priceMode === "teams"} onClick={() => setPriceMode("teams")} data-testid="planner-teams">
              With Teams
            </button>
            <button type="button" aria-pressed={priceMode === "noTeams"} onClick={() => setPriceMode("noTeams")} data-testid="planner-noteams">
              No Teams
            </button>
          </div>
          <button type="button" className="tb-btn sm ghost" onClick={reset} data-testid="planner-reset">
            <RotateCcw size={13} /> Reset
          </button>
          <button type="button" className="tb-btn sm" onClick={exportPdf} disabled={!!exporting} data-testid="planner-export-pdf" title="Download a branded PDF proposal of this target state">
            {exporting ? <Loader2 size={13} className="spin" /> : <FileDown size={13} />} Export PDF
          </button>
          <label className="ws-check small" title="Append the affected-user lists (gap and security findings) to the PDF" data-testid="planner-appendix-toggle">
            <input type="checkbox" checked={includeAppendix} onChange={(e) => setIncludeAppendix(e.target.checked)} /> Appendix: affected users
          </label>
        </div>
        <div className="planner-totals">
          <div className="ptotal">
            <span>Current / mo</span>
            <b className="mono" data-testid="planner-current">
              {formatPrice(plan.currentMonthly, symbol, plan.estimated)}
            </b>
          </div>
          <div className="ptotal">
            <span>Target / mo</span>
            <b className="mono" data-testid="planner-target">
              {formatPrice(plan.targetMonthly, symbol, plan.estimated)}
            </b>
          </div>
          <div className={`ptotal primary ${plan.deltaMonthly > 0 ? "up" : plan.deltaMonthly < 0 ? "down" : ""}`}>
            <span>Delta / mo</span>
            <b className="mono" data-testid="planner-delta">
              {signed(plan.deltaMonthly, symbol, plan.estimated)}
            </b>
            <small className="mono">{signed(plan.deltaMonthly * 12, symbol, plan.estimated)} / yr</small>
            {plan.retireMonthly > 0 && (
              <small className="mono" data-testid="planner-delta-breakdown">
                licenses {signed(plan.deltaMonthly + plan.retireMonthly, symbol)} · retired −{formatPrice(plan.retireMonthly, symbol)}
              </small>
            )}
          </div>
          <div className="ptotal">
            <span>Features</span>
            <b>
              <span className="ok">+{plan.gains}</span> {plan.losses ? <span className="warn">−{plan.losses}</span> : null}
            </b>
            {plan.retireMonthly > 0 && <small className="ok mono">retire −{formatPrice(plan.retireMonthly, symbol)} / mo</small>}
          </div>
        </div>
      </div>

      {plan.unpriced.length > 0 && (
        <div className="ws-alert">
          <Info size={13} /> {plan.unpriced.length} SKU(s) have no list price in the dataset and are excluded from totals: {plan.unpriced.map((u) => u.sku.name).join(", ")}.
        </div>
      )}

      {plan.lines.some((l) => l.remediation) && (
        <div className="ws-alert" data-testid="planner-remediation-note">
          <Wrench size={13} /> Gap-fix moves were pre-filled with the cheapest option per license family. Switch a target or add-on to compare alternatives, then save the scenario.
        </div>
      )}
      <div className="move-list">
        {plan.lines.map((line) => (
          <PlannerMove key={line.from_sku} line={line} plans={plans} rows={dataset.rows} priceMode={priceMode} symbol={symbol} onChange={updateMove} readOnly={!isAdmin} addons={addons} />
        ))}
      </div>

      <section className="ws-card glass scenarios">
        <div className="ws-card-head">
          <h2>Scenarios</h2>
          {isAdmin && (
            <div className="ws-inline-form">
              <input className="ws-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="Scenario name, e.g. E3 to E5 for Finance" data-testid="scenario-name" />
              <button type="button" className="tb-btn primary sm" onClick={save} disabled={saving} data-testid="scenario-save">
                {saving ? <Loader2 size={13} className="spin" /> : <Save size={13} />} Save
              </button>
            </div>
          )}
        </div>
        {scenarios.length === 0 && <p className="muted">No saved scenarios yet.</p>}
        <ul className="scenario-list">
          {scenarios.map((s) => (
            <li key={s.id} data-testid={`scenario-${s.id}`} className={s.share_token ? "shared" : ""}>
              <div className="scenario-main">
                <div>
                  <span className="scenario-title">
                    <b>{s.name}</b>
                    {s.share_token && (
                      <span className="mini-chip ok" data-testid={`scenario-shared-${s.id}`}>
                        <Link2 size={10} /> shared
                      </span>
                    )}
                  </span>
                  <span className="muted">
                    {s.moves.filter((m) => m.to_plan || m.to_addons.length || (m.to_extras || []).length).length} changes · {s.region.toUpperCase()} · {s.price_mode === "teams" ? "With Teams" : "No Teams"} · {new Date(s.updated_at).toLocaleDateString()}
                  </span>
                </div>
                <div className="tenant-actions">
                  <button type="button" className="tb-btn sm" onClick={() => load(s)} data-testid="scenario-load">
                    <FolderOpen size={13} /> Load
                  </button>
                  {isAdmin && !s.share_token && (
                    <button type="button" className="tb-btn sm ghost" onClick={() => share(s)} disabled={sharing === s.id} title="Create a read-only public link" data-testid={`scenario-share-${s.id}`}>
                      {sharing === s.id ? <Loader2 size={13} className="spin" /> : <Link2 size={13} />} Share
                    </button>
                  )}
                  {isAdmin && (
                    <button type="button" className="tb-btn sm ghost danger" onClick={() => remove(s)} data-testid="scenario-delete">
                      <Trash2 size={13} />
                    </button>
                  )}
                </div>
              </div>
              {s.share_token && (
                <div className="share-row" data-testid={`scenario-link-${s.id}`}>
                  <input className="ws-input mono" readOnly value={`${window.location.origin}/p/${s.share_token}`} onFocus={(e) => e.target.select()} data-testid={`scenario-link-input-${s.id}`} />
                  <button type="button" className="tb-btn sm" onClick={() => copyLink(s)} data-testid={`scenario-copy-${s.id}`}>
                    <Copy size={13} /> Copy
                  </button>
                  <a className="tb-btn sm ghost" href={`/p/${s.share_token}`} target="_blank" rel="noreferrer" data-testid={`scenario-open-${s.id}`}>
                    <ExternalLink size={13} /> Open
                  </a>
                  {isAdmin && (
                    <>
                      <button type="button" className="tb-btn sm ghost" onClick={() => share(s)} disabled={sharing === s.id} title="Regenerate link (old link stops working)" data-testid={`scenario-regen-${s.id}`}>
                        <RotateCcw size={13} /> Regenerate
                      </button>
                      <button type="button" className="tb-btn sm ghost danger" onClick={() => share(s, true)} disabled={sharing === s.id} data-testid={`scenario-revoke-${s.id}`}>
                        <Link2Off size={13} /> Revoke
                      </button>
                    </>
                  )}
                </div>
              )}
            </li>
          ))}
        </ul>
      </section>
      <p className="lookup-foot">
        Prices: {pricing.note}. Deltas use list prices for the moved seats only; standalone add-on retirement is estimated from users who hold both the base suite and the add-on. Verify with Microsoft or your partner before purchasing.
      </p>
      {exporting === "render" && <ProposalCard cardRef={cardRef} tenant={tenant} scan={scan} plan={plan} plans={plans} rows={dataset.rows} priceMode={priceMode} pricing={pricing} scenarioName={name.trim()} appendix={appendix} />}
    </div>
  );
}
